import { Box, Tooltip, Typography } from "@mui/material";
import { formatDateStringToDDMMYYYY } from "helper/utils";
import { isNumber } from "lodash";
import {
  ApproveStatus,
  ApproveStatusValue,
  statusColor,
} from "modules/DeclareLeave/components/CustomCalendarTimeKeeping/const";
import { TYPE_LEAVE } from "modules/DeclareLeave/redux/getReasonLeave";
import { selectListReasonArriveLate } from "modules/DeclareLeave/redux/selector";
import { useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import pxToRem from "theme/base/pxToRem";
import { PopoverAction } from "../../LeaveApplication/PopoverAction";
import { DIALOG } from "helper/enum/dialogEnum";
import { useDialog } from "helper/customHook";
import { setRequestLeaveDetailId } from "modules/DeclareLeave/redux/getRequestLeaveDetail";
import {
  setIsUpdateLeaveMaternityOrder,
  updateTimeListConfig,
} from "modules/DeclareLeave/redux/updateLeaveMaternityOrder";

const useColumns = () => {
  const listReasonArriveLate = useSelector(selectListReasonArriveLate);
  const confirmEditDialog = useDialog(DIALOG.CONFIRM_EDIT);
  const dispatch = useDispatch();

  const renderColumnContent = (value: string, separator: string = "#") => {
    const items = (value ?? "").split(separator) ?? [];
    return (
      <Box display={"flex"} flexDirection={"column"}>
        {items.map((item, index) => (
          <Typography
            key={index}
            sx={(theme) => ({
              fontSize: pxToRem(12),
              color: theme.palette.text.textPrimary,
            })}
          >
            {item}
          </Typography>
        ))}
      </Box>
    );
  };

  const defaultColumns = [
    {
      id: "index",
      accessorKey: "index",
      header: "STT",
      width: 50,
      minSize: 50,

      cell: (info) => (
        <Typography
          sx={(theme) => ({
            fontSize: pxToRem(12),
            color: theme.palette.text.textPrimary,
            width: "100%",

            textAlign: "center",
          })}
        >
          {info.row.index + 1 < 10
            ? `0${info.row.index + 1}`
            : info.row.index + 1}
        </Typography>
      ),
      footer: (props) => props.column.id,
      pinPosition: "left",
    },

    {
      id: "workingAreaName",
      accessorKey: "workingAreaName",
      header: "Khu vực",
      footer: (props) => props.column.id,
      minSize: 100,
      width: 100,
      pinPosition: "center",
      enableSorting: false,
    },

    {
      id: "employeeCode",
      accessorKey: "employeeCode",
      header: "Mã NV",
      minSize: 150,
      pinPosition: "left",
      enableSorting: false,
    },
    {
      id: "employeeName",
      accessorKey: "employeeName",
      header: "Tên nhân viên",
      footer: (props) => props.column.id,
      minSize: 150,
      pinPosition: "center",
      enableSorting: false,
    },
    {
      id: "positionName",
      accessorKey: "positionName",
      header: "Chức danh",
      footer: (props) => props.column.id,
      minSize: 150,
      pinPosition: "left",
      enableSorting: false,
    },
    {
      id: "departmentLevel1Name",
      accessorKey: "departmentLevel1Name",
      header: "Đơn vị cấp 1",
      footer: (props) => props.column.id,
      minSize: 150,
      pinPosition: "left",
      enableSorting: false,
    },
    {
      id: "departmentLevel2Name",
      accessorKey: "departmentLevel2Name",
      header: "Đơn vị cấp 2",
      footer: (props) => props.column.id,
      minSize: 150,
      pinPosition: "center",
      enableSorting: false,
    },
  ];

  const arriveLateCols = useMemo(
    () => [
      ...defaultColumns,
      {
        id: "createdDate",
        accessorKey: "registerDate",
        header: "Ngày tạo đơn",
        cell: (info) => (
          <Typography
            sx={(theme) => ({
              fontSize: pxToRem(12),
              color: theme.palette.text.textPrimary,
              width: "100%",
              textAlign: "center",
            })}
          >
            {formatDateStringToDDMMYYYY(info.getValue())}
          </Typography>
        ),
        footer: (props) => props.column.id,
        minSize: 125,
        pinPosition: "left",
      },
      {
        id: "type",
        accessorKey: "type",
        header: "Loại",

        cell: (info) => {
          const listType = (info.getValue() ?? "").split("#") ?? [];
          return (
            <Box display={"flex"} flexDirection={"column"}>
              {listType.map((type, index) => (
                <Typography
                  key={index}
                  sx={(theme) => ({
                    fontSize: pxToRem(12),
                    color: theme.palette.text.textPrimary,
                  })}
                >
                  {type}
                </Typography>
              ))}
            </Box>
          );
        },
        footer: (props) => props.column.id,
        minSize: 120,
        enablePinning: true,
        pinPosition: "right",
      },
      {
        accessorKey: "dayOffs",
        header: () => "Thời gian",
        footer: (props) => props.column.id,
        cell: (info) => {
          const dayOffs = (info.getValue() ?? "").split("#") ?? [];
          return (
            <Box display={"flex"} flexDirection={"column"}>
              {dayOffs.map((type, index) => (
                <Typography
                  key={index}
                  sx={(theme) => ({
                    fontSize: pxToRem(12),
                    color: theme.palette.text.textPrimary,
                  })}
                >
                  {type}
                </Typography>
              ))}
            </Box>
          );
        },
        minSize: 230,
      },
      {
        accessorKey: "totalMinutes",
        header: () => <span>Số phút</span>,
        footer: (props) => props.column.id,
        cell: (info) => (
          <Typography
            sx={(theme) => ({
              fontSize: pxToRem(12),
              color: theme.palette.text.textPrimary,
              width: "100%",
              textAlign: "center",
            })}
          >
            {info.getValue()}
          </Typography>
        ),
        minSize: 90,
      },
      {
        accessorKey: "reason",
        header: "Lý do đi muộn/về sớm",
        cell: (info) => {
          return (
            <Box
              display={"flex"}
              flexDirection={"column"}
              alignItems={"flex-end"}
            >
              <Typography
                sx={(theme) => ({
                  fontSize: pxToRem(12),
                  color: theme.palette.text.textPrimary,
                })}
              >
                {info.getValue()}
              </Typography>
            </Box>
          );
        },
        footer: (props) => props.column.id,
        minSize: 180,
      },
      {
        accessorKey: "relatedPersonName",
        header: "Người liên quan",
        footer: (props) => props.column.id,
        minSize: 150,
      },
      {
        accessorKey: "approveName",
        header: "Người duyệt",
        footer: (props) => props.column.id,
        minSize: 150,
      },
      {
        id: "modifiedDate",
        accessorKey: "modifiedDate",
        header: "Ngày cập nhật đơn",
        cell: (info) => (
          <Typography
            sx={(theme) => ({
              fontSize: pxToRem(12),
              color: theme.palette.text.textPrimary,
              width: "100%",
              textAlign: "center",
            })}
          >
            {info.getValue() ? formatDateStringToDDMMYYYY(info.getValue()) : ""}
          </Typography>
        ),
        footer: (props) => props.column.id,
        minSize: 155,
        pinPosition: "left",
      },
      {
        accessorKey: "note",
        header: "Lý do chi tiết",
        minSize: 180,
        cell: (info) => (
          <Tooltip title={info?.getValue()}>
            <Typography
              sx={{
                fontSize: pxToRem(12),
                overflow: "hidden",
                textOverflow: "ellipsis",
                textAlign: "left",
              }}
            >
              {info?.getValue()?.length > 60
                ? `${info?.getValue()?.slice(0, 60)}...`
                : info?.getValue()}
            </Typography>
          </Tooltip>
        ),
        footer: (props) => props.column.id,
      },
      {
        accessorKey: "refuseReason",
        header: "Lý do phê duyệt",
        footer: (props) => props.column.id,
        minSize: 150,
      },
      {
        accessorKey: "status",
        header: "Trạng thái",
        footer: (props) => props.column.id,
        cell: (info) => {
          const status = info.getValue();
          return (
            <Typography
              sx={(theme) => ({
                fontSize: pxToRem(12),
                fontWeight: 600,
                color: statusColor(theme, status, "color"),
                width: "100%",
                textAlign: "center",
              })}
            >
              {ApproveStatus[status]}
            </Typography>
          );
        },
        minSize: 100,
      },
    ],
    [defaultColumns, listReasonArriveLate]
  );

  const leaveCols = useMemo(
    () => [
      ...defaultColumns,
      {
        id: "createdDate",
        accessorKey: "registerDate",
        header: "Ngày tạo đơn",
        cell: (info) => (
          <Typography
            sx={(theme) => ({
              fontSize: pxToRem(12),
              color: theme.palette.text.textPrimary,
              width: "100%",
              textAlign: "center",
            })}
          >
            {formatDateStringToDDMMYYYY(info.getValue())}
          </Typography>
        ),
        footer: (props) => props.column.id,
        minSize: 125,
        pinPosition: "left",
      },
      {
        id: "type",
        accessorKey: "type",
        header: "Loại nghỉ",
        cell: (info) => renderColumnContent(info.getValue()),
        footer: (props) => props.column.id,
        minSize: 120,
        enablePinning: true,
      },
      {
        accessorKey: "dayOffs",
        header: () => "Ngày nghỉ",
        footer: (props) => props.column.id,
        cell: (info) => renderColumnContent(info.getValue()),
        minSize: 250,
      },
      {
        accessorKey: "totalLeaveDay",
        header: () => <span>Số ngày nghỉ</span>,
        footer: (props) => props.column.id,
        cell: (info) => (
          <Typography
            sx={(theme) => ({
              fontSize: pxToRem(12),
              color: theme.palette.text.textPrimary,
              width: "100%",
              textAlign: "center",
            })}
          >
            {isNumber(info.getValue()) &&
            info.getValue() < 10 &&
            info.getValue() >= 1
              ? `0${info.getValue()}`
              : info.getValue()}
          </Typography>
        ),
        minSize: 120,
      },
      {
        accessorKey: "leaveReason",
        header: "Lý do nghỉ",
        cell: (info) => (
          <Tooltip title={info?.getValue()}>
            <Typography
              sx={{
                fontSize: pxToRem(12),
                overflow: "hidden",
                textOverflow: "ellipsis",
                textAlign: "left",
              }}
            >
              {info?.getValue()?.length > 60
                ? `${info?.getValue()?.slice(0, 60)}...`
                : info?.getValue()}
            </Typography>
          </Tooltip>
        ),
        footer: (props) => props.column.id,
        minSize: 180,
      },
      {
        accessorKey: "relationPersonName",
        header: "Người liên quan",
        footer: (props) => props.column.id,
        minSize: 150,
      },
      {
        accessorKey: "approveName",
        header: "Người duyệt",
        footer: (props) => props.column.id,
        cell: (info) => renderColumnContent(info.getValue(), ","),
        minSize: 150,
      },
      {
        id: "modifiedDate",
        accessorKey: "modifiedDate",
        header: "Ngày cập nhật đơn",
        cell: (info) => (
          <Typography
            sx={(theme) => ({
              fontSize: pxToRem(12),
              color: theme.palette.text.textPrimary,
              width: "100%",
              textAlign: "center",
            })}
          >
            {info.getValue() ? formatDateStringToDDMMYYYY(info.getValue()) : ""}
          </Typography>
        ),
        footer: (props) => props.column.id,
        minSize: 155,
        pinPosition: "left",
      },
      {
        accessorKey: "modifierName",
        header: "Người cập nhật",
        footer: (props) => props.column.id,
        minSize: 150,
      },
      {
        accessorKey: "status",
        header: "Trạng thái",
        footer: (props) => props.column.id,
        cell: (info) => {
          const status = info.getValue();
          return (
            <Typography
              sx={(theme) => ({
                fontSize: pxToRem(12),
                fontWeight: 600,
                color: statusColor(theme, status, "color"),
                textAlign: "center",
                width: "100%",
              })}
            >
              {ApproveStatus[status]}
            </Typography>
          );
        },
        minSize: 100,
      },
      {
        accessorKey: "action",
        header: "",
        cell: (info) => (
          <PopoverAction
            showAction={[
              ApproveStatusValue.PENDING,
              ApproveStatusValue.APPROVE,
              ApproveStatusValue.AUTO_APPROVE,
            ].includes(info.row.original.status)}
            editable={info.row.original.editable}
            isDeletable={false}
            editAction={(e) => {
              e.stopPropagation();
              const month = (info.row.original.dayOffs ?? "")
                .split("#")[0]
                .split("/")[1];
              const year = info.row.original.dayOffs
                ?.split("đến ")[0]
                ?.split("/")
                ?.pop();
              confirmEditDialog.open();
              dispatch(
                updateTimeListConfig({
                  month,
                  year,
                  employeeCode: info.row.original.employeeCode,
                })
              );
              dispatch(setIsUpdateLeaveMaternityOrder(true));
              dispatch(setRequestLeaveDetailId(info.row.original.id));
            }}
            deleteAction={() => {}}
          />
        ),
        minSize: 70,
      },
    ],
    []
  );
  const responseCols = useMemo(
    () => [
      ...defaultColumns,
      {
        id: "createdDate",
        accessorKey: "createdDate",
        header: "Ngày tạo đơn",
        cell: (info) => (
          <Typography
            sx={(theme) => ({
              fontSize: pxToRem(12),
              color: theme.palette.text.textPrimary,
              width: "100%",
              textAlign: "center",
            })}
          >
            {formatDateStringToDDMMYYYY(info.getValue())}
          </Typography>
        ),
        footer: (props) => props.column.id,
        minSize: 125,
        pinPosition: "left",
      },
      {
        id: "date",
        accessorKey: "date",
        header: "Ngày giải trình",
        footer: (props) => props.column.id,
        cell: (info) => (
          <Typography
            sx={(theme) => ({
              fontSize: pxToRem(12),
              color: theme.palette.text.textPrimary,
              width: "100%",
              textAlign: "center",
            })}
          >
            {formatDateStringToDDMMYYYY(info.getValue())}
          </Typography>
        ),
        minSize: 120,
        pinPosition: "left",
      },
      {
        id: "type",
        accessorKey: "type",
        header: "Công làm việc",
        minSize: 120,
        pinPosition: "left",
        cell: (info) => {
          return (
            <Typography
              sx={(theme) => ({
                fontSize: pxToRem(12),
                color: theme.palette.text.textPrimary,
                width: "100%",
                textAlign: "center",
              })}
            >
              {info.getValue() === TYPE_LEAVE.FULL_DAY ? "Cả ngày" : "Nửa ngày"}
            </Typography>
          );
        },
      },
      {
        accessorKey: "description",
        header: "Lý do giải trình",
        cell: (info) => {
          return (
            <Tooltip title={info?.getValue()}>
              <Typography
                sx={{
                  fontSize: pxToRem(12),
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  textAlign: "left",
                }}
              >
                {info?.getValue()?.length > 60
                  ? `${info?.getValue()?.slice(0, 60)}...`
                  : info?.getValue()}
              </Typography>
            </Tooltip>
          );
        },
        footer: (props) => props.column.id,
        minSize: 220,
      },
      {
        id: "approverName",
        accessorKey: "approverName",
        header: "Người phê duyệt",
        minSize: 160,
        pinPosition: "left",
      },
      {
        id: "refuseReason",
        accessorKey: "refuseReason",
        header: "Ghi chú",
        minSize: 160,
        pinPosition: "left",
      },
      {
        accessorKey: "confirmDate",
        header: () => "Ngày phản hồi",
        footer: (props) => props.column.id,
        cell: (info) => {
          return (
            <Typography
              sx={(theme) => ({
                fontSize: pxToRem(12),
                color: theme.palette.text.textPrimary,
                width: "100%",
                textAlign: "center",
              })}
            >
              {info.getValue()
                ? formatDateStringToDDMMYYYY(info.getValue())
                : "---"}
            </Typography>
          );
        },
        minSize: 125,
      },

      {
        accessorKey: "confirmStatus",
        header: "Trạng thái",
        footer: (props) => props.column.id,
        cell: (info) => {
          const status = info.getValue();
          return (
            <Typography
              sx={(theme) => ({
                fontWeight: 600,
                fontSize: pxToRem(12),
                color: statusColor(theme, status, "color"),
              })}
            >
              {ApproveStatus[status]}
            </Typography>
          );
        },
        minSize: 100,
      },
    ],
    []
  );

  return { arriveLateCols, leaveCols, responseCols };
};
export default useColumns;
